import { ADMIN_ROLE } from '@careapp-group/core';
import { Action, createSelector } from '@ngrx/store';

import * as actions from '../actions/index';

import { KratosAuth, State, initialState } from './auth.state';
import { mergeReducers } from '../entity-state/merge-reducers';
import { authLoginReducer } from './login/login.reducer';
import { authLogoutReducer } from './logout/logout.reducer';
import { authRecoveryReducer } from './recovery/recovery.reducer';
import { authRegistrationReducer } from './registration/registration.reducer';
import { authSettingsReducer } from './settings/settings.reducer';
import { authVerificationReducer } from './verification/verification.reducer';

export const LOCAL_STORAGE_AUTH_KEY = 'careapp.auth';

function baseReducer(state: State = initialState, action: any): State {
  switch (action.type) {
    case actions.RequestLoggedInUser.type:
    case actions.RequestSaveProfile.type:
    case actions.REQUEST_SET_PHONE_NUMBER: {
      return { ...state, loading: true, resetPassword: false };
    }
    case actions.REQUEST_VERIFICATION: {
      return { ...state, loading: true, requestingVerification: true };
    }

    case actions.CLEAR_VERIFICATION: {
      return { ...state, requestingVerification: false, currentVerification: null };
    }

    case actions.AuthResetErrorsAction.type: {
      return { ...state, error: '', resetPassword: false };
    }

    case actions.RESPONSE_SUCCESS_UPDATED_PROFILE:
    case actions.ResponseSuccessSaveProfile.type: {
      return { ...state, user: action.payload, loading: false };
    }

    case actions.ResponseSuccessLoggedInUser.type: {
      return { ...state, user: action.payload, ready: true };
    }

    case actions.ApplicationAuthorized.type: {
      if (action.user) {
        return { ...state, user: action.user, ready: true, loading: false };
      }
      return {
        ...state,
        ready: true,
      };
    }

    case actions.ResponseSuccessUserRoles.type:
      return { ...state, roles: action.payload };

    case actions.HydrateAuth.type: {
      return {
        ...state,
        auth: {
          ...(state.auth || {}),
          ...action.payload,
          dirty: true,
          date: new Date(),
        },
      };
    }

    case actions.RequestUpdateMemberMetaAction.type: {
      return { ...state, loading: true };
    }

    case actions.ResponseFailUpdateMemberMetaAction.type: {
      return { ...state, loading: false, error: action.payload };
    }

    case actions.ResponseSuccessUpdateMemberMetaAction.type: {
      const user = { ...state.user };
      user.meta = action.payload;
      return { ...state, auth: { ...state.auth }, user, loading: false };
    }

    case actions.RESPONSE_FAIL_SET_PHONE_NUMBER:
    case actions.RESPONSE_FAIL_SET_EMAIL_ADDRESS:
    case actions.RESPONSE_FAIL_VERIFICATION: {
      return {
        ...state,
        loading: false,
        requestingPasswordResetToken: false,
        requestingVerification: false,
        error: action.payload,
      };
    }

    case actions.RESPONSE_SUCCESS_VERIFICATION: {
      return {
        ...state,
        currentVerification: action.payload,
        loading: false,
        requestingVerification: false,
        error: '',
      };
    }

    case actions.RESPONSE_SUCCESS_SET_PHONE_NUMBER: {
      const updatedVerification = { ...state.currentVerification };
      updatedVerification.status = 'complete';
      return {
        ...state,
        user: action.payload,
        currentVerification: updatedVerification,
        loading: false,
      };
    }

    case actions.RESPONSE_SUCCESS_SET_EMAIL_ADDRESS: {
      const updatedVerification = { ...state.currentVerification };
      updatedVerification.status = 'complete';
      return {
        ...state,
        user: action.payload,
        currentVerification: updatedVerification,
        loading: false,
      };
    }

    case actions.RESET_STATE:
      return initialState;

    default:
      return state;
  }
}

export const mergedReducer = mergeReducers(
  baseReducer,
  authLoginReducer,
  authLogoutReducer,
  authRecoveryReducer,
  authRegistrationReducer,
  authSettingsReducer,
  authVerificationReducer
);
export function reducer(state: State | undefined, action: Action) {
  return mergedReducer(state, action);
}

export const createSelectors = (rootReducer: any) => ({
  getLoggedIn: createSelector(
    rootReducer,
    (state: State): boolean => state.auth && (<KratosAuth>state.auth).session?.active === true
  ),
  getAuth: createSelector(rootReducer, (state: State) => state.auth),
  getError: createSelector(rootReducer, (state: State) => state.error),
  getLoading: createSelector(rootReducer, (state: State) => state.loading),
  getPersisted: createSelector(rootReducer, (state: State) => {
    const persisted = { ...state };
    delete persisted.ready;
    delete persisted.loading;
    return persisted;
  }),
  getResetPassword: createSelector(rootReducer, (state: State) => state.resetPassword),
  getLoggedInUser: createSelector(rootReducer, (state: State) => state.user),
  getRoles: createSelector(rootReducer, (state: State) => state.roles),
  isAdmin: createSelector(
    rootReducer,
    (state: State) => state.roles && !!state.roles.find((value) => value === ADMIN_ROLE)
  ),
  isReady: createSelector(rootReducer, (state: State) => (state ? state.ready : false)),
  getLatestSeenReleaseNoteGuid: createSelector(rootReducer, (state: State) =>
    state ? state.user.meta['latestReleaseNoteSeen'] : null ?? null
  ),
  getAnnouncementPaneHintDismissed: createSelector(rootReducer, (state: State) =>
    state ? state.user.meta['announcementPaneHintDismissed'] : null ?? null
  ),
});
